import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import api from '../../../utils/callApi'
import { filterEphemeralPvpRosterCharacters } from '../../../utils/rosterCharacters'

// import type { RootState } from '../../../store'
import { global } from '../../global'

import { applyWeaponOp, type EmbedWeaponSlot } from '../character/pendingWeaponOps'
import { confirmWeaponOp } from '../character/reducer'

import type { GameStateProps, StockProps } from './state'
import initialState from './state'

export const gameSlice = createSlice({
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  name: 'game',
  reducers: {
    decrement: (state: GameStateProps, action: PayloadAction<string>) => {
      if (state.stock[action.payload as keyof StockProps] === 0) {
        return
      }
      state.stock[action.payload as keyof StockProps] -= 1
    },
    increment: (state: GameStateProps, action: PayloadAction<string>) => {
      state.stock[action.payload as keyof StockProps] += 1
    },
    setGameStatus: (state: GameStateProps, action: PayloadAction<number>) => {
      state.gameState = action.payload
    },
    setGameTurn: (state: GameStateProps) => {
      state.turn = !state.turn
    },
    setAtkBtnState: (state: GameStateProps, action: PayloadAction<boolean>) => {
      state.attackBtnState = action.payload
    },
    setInventoryStatus: (
      state: GameStateProps,
      action: PayloadAction<boolean>,
    ) => {
      state.inventoryOpened = action.payload
    },
    setCharacterStatus: (
      state: GameStateProps,
      action: PayloadAction<boolean>,
    ) => {
      state.characterOpened = action.payload
    },
    setAddress: (state: GameStateProps, action: PayloadAction<string>) => {
      state.address = action.payload
    },
    addExp: (state: GameStateProps, action: PayloadAction<Number>) => {
      const address = state.address
      const res = api(`user/add/exp`, 'post', {
        walletAddress: address,
        amount: action.payload,
      })
    },
    setSecondTurn: (state: GameStateProps) => {
      state.secondTurn = 1
    },
    addTurn: (state: GameStateProps) => {
      if (state.secondTurn !== 0)
        if (global.currentCharacterName === 'siren-2')
          state.secondTurn = Math.floor((state.secondTurn + 1) % 3)
        else {
          state.secondTurn = Math.floor((state.secondTurn + 1) % 4)
        }
      if (state.thirdTurn !== 0) {
        if (global.currentCharacterName === 'siren-2') {
          state.thirdTurn = Math.floor((state.thirdTurn + 1) % 4)
        } else {
          state.thirdTurn = Math.floor((state.thirdTurn + 1) % 5)
        }
      }
    },
    setThirdTurn: (state: GameStateProps) => {
      state.thirdTurn = 1
    },
    setTurnFormat: (state: GameStateProps) => {
      state.thirdTurn = 0
      state.secondTurn = 0
    },
    setLoadingStatus: (
      state: GameStateProps,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoading = action.payload
    },
    getCharacterStatus: (
      state: GameStateProps,
      action: PayloadAction<boolean>,
    ) => {
      state.getCharacter = action.payload
    },
    openChapterPage: (
      state: GameStateProps,
      action: PayloadAction<boolean>,
    ) => {
      state.openChapter = action.payload
    },
    setDisplay: (state: GameStateProps, action: PayloadAction<string>) => {
      state.display = action.payload
    },
    setRememberCode: (
      state: GameStateProps,
      action: PayloadAction<boolean>,
    ) => {
      state.rememberCode = action.payload
    },
    setButtonView: (state: GameStateProps, action: PayloadAction<boolean>) => {
      state.buttonView = action.payload
    },
    setAttackAlert: (state: GameStateProps, action: PayloadAction<boolean>) => {
      state.attackAlert = action.payload
    },
    setSkullSet: (state: GameStateProps, action: PayloadAction<boolean>) => {
      state.skullSet = action.payload
    },
    setWeaponAttackStatus: (
      state: GameStateProps,
      action: PayloadAction<boolean>,
    ) => {
      state.weaponAttackStatus = action.payload
    },
    setNickname: (state: GameStateProps, action: PayloadAction<string>) => {
      state.nickname = action.payload
    },
    setBattleConfigs: (state: GameStateProps, action: PayloadAction<any[]>) => {
      state.battleConfigs = action.payload
    },
    // ✨ NEW: Character Instance Actions
    setUserCharacters: (state: GameStateProps, action: PayloadAction<any[]>) => {
      state.userCharacters = filterEphemeralPvpRosterCharacters(action.payload)
    },
    setSelectedCharacter: (state: GameStateProps, action: PayloadAction<any | null>) => {
      state.selectedCharacter = action.payload
    },
    updateCharacterProgress: (state: GameStateProps, action: PayloadAction<{ characterId: string, progress: any }>) => {
      const character = state.userCharacters.find(c => c._id === action.payload.characterId)
      if (character) {
        // campaignProgress is now user-level, not character-level
        character.level = action.payload.progress.level
        character.experience = action.payload.progress.experience
        character.stats = action.payload.progress.stats
        
        // Update selected character if it's the same one
        if (state.selectedCharacter?._id === action.payload.characterId) {
          state.selectedCharacter = { ...character }
        }
      }
    },
    patchUserCharacterHpAfterHospitalHeal: (
      state: GameStateProps,
      action: PayloadAction<{ characterId: string; currentHp: number }>,
    ) => {
      const id = String(action.payload.characterId ?? '')
      if (!id) return
      const hp = Number(action.payload.currentHp)
      if (!Number.isFinite(hp) || hp <= 0) return
      const character = state.userCharacters.find((c: any) => String(c?._id ?? '') === id)
      if (!character) return
      if (!character.stats || typeof character.stats !== 'object') character.stats = {}
      character.stats.currentHp = hp
      if (state.selectedCharacter && String(state.selectedCharacter._id ?? '') === id) {
        const sel = state.selectedCharacter as any
        if (!sel.stats || typeof sel.stats !== 'object') sel.stats = {}
        sel.stats.currentHp = hp
      }
    },
    setLoggedIn: (state: GameStateProps, action: PayloadAction<{ loggedIn: boolean }>) => {
      state.loggedIn = action.payload.loggedIn
    },
    setUserCampaignProgress: (state: GameStateProps, action: PayloadAction<{ highestRoundCompleted: number, lastPlayedRound?: number, currentArea: string }>) => {
      state.userCampaignProgress = action.payload
    }
  },
  extraReducers: (builder) => {
    /**
     * An equip the server acknowledged is folded into the character it belongs to, in the same
     * action that drops it from `character.pendingWeaponOps` — so the slot the player is looking at
     * never changes as ownership of the change moves from the overlay to server state.
     */
    builder.addCase(confirmWeaponOp, (state: GameStateProps, action) => {
      const op = action.payload
      const id = String(op.characterId ?? '')
      if (!id) return
      const fold = (character: any) => {
        character.embedWeapons = applyWeaponOp(
          (character.embedWeapons ?? []) as EmbedWeaponSlot[],
          op,
        )
      }
      const character = state.userCharacters.find((c: any) => String(c?._id ?? '') === id)
      if (character) fold(character)
      const selected = state.selectedCharacter as any
      if (selected && String(selected._id ?? '') === id) fold(selected)
    })
  },
})

export const {
  setAddress,
  setGameStatus,
  setGameTurn,
  setAtkBtnState,
  setTurnFormat,
  setInventoryStatus,
  setCharacterStatus,
  increment,
  decrement,
  addExp,
  addTurn,
  setSecondTurn,
  setThirdTurn,
  setLoadingStatus,
  getCharacterStatus,
  openChapterPage,
  setDisplay,
  setRememberCode,
  setButtonView,
  setAttackAlert,
  setSkullSet,
  setWeaponAttackStatus,
  setNickname,
  setBattleConfigs,
  // ✨ NEW: Character Instance Actions
  setUserCharacters,
  setSelectedCharacter,
  updateCharacterProgress,
  patchUserCharacterHpAfterHospitalHeal,
  setLoggedIn,
  setUserCampaignProgress,
} = gameSlice.actions

export default gameSlice.reducer
